import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Play, Pause, Eye } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { storiesService, StoryGroup, StoryView } from '../services/storiesService';
import { useLanguage } from '../context/LanguageContext';

interface StoryViewerProps {
  storyGroup: StoryGroup;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
}

const StoryViewer: React.FC<StoryViewerProps> = ({
  storyGroup,
  onClose,
  onNext,
  onPrevious
}) => {
  const { user } = useAuth();
  const { select, locale } = useLanguage();
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showViewers, setShowViewers] = useState(false);
  const [storyViews, setStoryViews] = useState<StoryView[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartRef = useRef<number>(0);

  const currentStory = storyGroup.stories[currentStoryIndex];
  const isMyStory = currentStory?.user_id === user?.id;

  useEffect(() => {
    if (!currentStory) return;

    // Mark story as viewed (only if not my story)
    if (!isMyStory) {
      void storiesService.viewStory(currentStory.id);
    }

    // Reset progress
    setProgress(0);
    setIsPaused(false);

    // Start progress timer
    startProgressTimer();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStoryIndex, currentStory]);

  const startProgressTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    const duration = (currentStory?.duration || 5) * 1000; // Convert to milliseconds
    const interval = 50; // Update every 50ms for smooth animation

    intervalRef.current = setInterval(() => {
      if (!isPaused) {
        setProgress(prev => {
          const newProgress = prev + (interval / duration) * 100;
          if (newProgress >= 100) {
            nextStory();
            return 0;
          }
          return newProgress;
        });
      }
    }, interval);
  };

  const nextStory = () => {
    if (currentStoryIndex < storyGroup.stories.length - 1) {
      setCurrentStoryIndex(prev => prev + 1);
    } else if (onNext) {
      onNext();
    } else {
      onClose();
    }
  };

  const previousStory = () => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(prev => prev - 1);
    } else if (onPrevious) {
      onPrevious();
    }
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  const handleStoryClick = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickPosition = clickX / rect.width;

    if (clickPosition < 0.3) {
      previousStory();
    } else if (clickPosition > 0.7) {
      nextStory();
    } else {
      togglePause();
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStartRef.current - touchEnd;

    if (Math.abs(diff) > 50) { // Minimum swipe distance
      if (diff > 0) {
        nextStory();
      } else {
        previousStory();
      }
    }
  };

  const loadStoryViews = async () => {
    if (!isMyStory || !currentStory) return;

    try {
      const views = await storiesService.getStoryViews(currentStory.id);
      setStoryViews(views);
      setShowViewers(true);
    } catch (err) {
      console.error('Error loading story views:', err);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return select({ es: 'Ahora', en: 'Now' });
    if (diffInMinutes < 60) {
      return `${diffInMinutes}${select({ es: 'm', en: 'm' })}`;
    }
    if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}${select({ es: 'h', en: 'h' })}`;
    }
    return date.toLocaleDateString(locale);
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (!currentStory) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black z-[9999] flex items-center justify-center">
      {/* Story Content */}
      <div
        className="relative w-full h-full max-w-md mx-auto bg-black cursor-pointer select-none flex flex-col"
        onClick={handleStoryClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Progress Bars */}
        <div className="absolute top-4 left-4 right-4 z-20 flex space-x-1">
          {storyGroup.stories.map((_, index) => (
            <div key={index} className="flex-1 h-1 bg-white bg-opacity-30 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all duration-100 ease-linear"
                style={{
                  width: index === currentStoryIndex
                    ? `${progress}%`
                    : index < currentStoryIndex ? '100%' : '0%'
                }}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="absolute top-8 left-4 right-4 z-20 flex items-center justify-between mt-6">
          <div className="flex items-center space-x-3">
            <img
              src={storyGroup.user.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=40&h=40&fit=crop'}
              alt={storyGroup.user.full_name}
              className="w-10 h-10 rounded-full border-2 border-white object-cover"
            />
            <div>
              <h3 className="text-white font-semibold text-sm">{storyGroup.user.username}</h3>
              <p className="text-white text-xs opacity-75">
                {formatTimeAgo(currentStory.created_at)}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {isPaused ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  togglePause();
                }}
                className="p-1"
              >
                <Play className="text-white" size={20} />
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  togglePause();
                }}
                className="p-1"
              >
                <Pause className="text-white" size={20} />
              </button>
            )}

            {isMyStory && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  loadStoryViews();
                }}
                className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              >
                <Eye className="text-white" size={20} />
              </button>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
            >
              <X className="text-white" size={24} />
            </button>
          </div>
        </div>

        {/* Story Content */}
        <div className="flex-1 flex items-center justify-center relative">
          {currentStory.content_type === 'image' && currentStory.content_url && (
            <img
              src={currentStory.content_url}
              alt={select({ es: 'Story', en: 'Story' })}
              className="max-w-full max-h-full object-contain"
              onError={(e) => {
                console.error('Error loading story image:', currentStory.content_url);
                e.currentTarget.style.display = 'none';
              }}
            />
          )}

          {currentStory.content_type === 'video' && currentStory.content_url && (
            <video
              src={currentStory.content_url}
              autoPlay
              muted
              className="max-w-full max-h-full object-contain"
              onEnded={nextStory}
              onError={(e) => {
                console.error('Error loading story video:', currentStory.content_url);
                e.currentTarget.style.display = 'none';
              }}
            />
          )}

          {currentStory.content_type === 'text' && (
            <div
              className="w-full h-full flex items-center justify-center p-8 text-center"
              style={{ backgroundColor: currentStory.background_color || '#9333ea' }}
            >
              <p className="text-white text-2xl font-bold leading-relaxed max-w-sm">
                {currentStory.text_content}
              </p>
            </div>
          )}

          {/* Fallback if no content */}
          {!currentStory.content_url && !currentStory.text_content && (
            <div className="w-full h-full flex items-center justify-center bg-gray-800">
              <p className="text-white text-center">
                {select({ es: 'Contenido no disponible', en: 'Content unavailable' })}
              </p>
            </div>
          )}
        </div>

        {/* Navigation hints (invisible areas for clicks) */}
        <div className="absolute inset-0 z-10 flex">
          <div className="w-1/3 h-full" />
          <div className="w-1/3 h-full" />
          <div className="w-1/3 h-full" />
        </div>

        {/* Previous/Next buttons for desktop */}
        <div className="hidden md:block absolute inset-0 pointer-events-none">
          {(currentStoryIndex > 0 || onPrevious) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                previousStory();
              }}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-75 transition-all pointer-events-auto"
            >
              <ChevronLeft size={24} />
            </button>
          )}

          {(currentStoryIndex < storyGroup.stories.length - 1 || onNext) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextStory();
              }}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-75 transition-all pointer-events-auto"
            >
              <ChevronRight size={24} />
            </button>
          )}
        </div>
      </div>

      {/* Story Views Modal */}
      {showViewers && (
        <div className="absolute inset-0 bg-black bg-opacity-75 flex items-end justify-center z-[10000]">
          <div className="bg-white rounded-t-xl w-full max-w-md max-h-96 overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">
                {select({ es: 'Visto por', en: 'Viewed by' })} {storyViews.length} {(storyViews.length === 1 ? select({ es: 'persona', en: 'person' }) : select({ es: 'personas', en: 'people' }))}
              </h3>
              <button
                onClick={() => setShowViewers(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X size={20} />
              </button>
            </div>

            <div className="overflow-y-auto max-h-80">
              {storyViews.map((view) => (
                <div key={view.id} className="flex items-center gap-3 p-4 hover:bg-gray-50">
                  <img
                    src={view.profiles.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=40&h=40&fit=crop'}
                    alt={view.profiles.full_name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{view.profiles.full_name}</p>
                    <p className="text-sm text-gray-500">@{view.profiles.username}</p>
                  </div>
                  <span className="text-xs text-gray-400">
                    {formatTimeAgo(view.viewed_at)}
                  </span>
                </div>
              ))}

              {storyViews.length === 0 && (
                <div className="p-8 text-center">
                  <Eye size={32} className="mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-500">{select({ es: 'Aún no hay visualizaciones', en: 'No views yet' })}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoryViewer;
